import { IArea } from "./Area.type";
import { IBase } from "./Base";
import { IDimension } from "./Dimension.type";
import { ECeaSeniorityStatus } from "./Role.type";
import { ECurrency } from "./Task.type";
import { IUser } from "./User.types";
import { EEmployeeContractType } from "./Vacation.type";
import { IVendor, IVendorClient } from "./Vendor.types";

export interface IWorkExperience {
  title: string;
  description: string;
  date: string;
  company: string;
}

export interface IEducation {
  title: string;
  description: string;
  dateStart: string;
  dateEnd: string;
}

export interface ILanguage {
  name: string;
  level: string;
}

export interface ICertification {
  name?: string;
  date?: string;
}

export interface IApplicantLinks {
  icon: string;
  url: string;
}

export interface IAICV {
  ofuscatedName: string;
  tagLine: string;
  workExperience: IWorkExperience;
  techSkills: string[];
  education: IEducation;
  languages: ILanguage[];
  extraWorkExperience?: IWorkExperience[];
  extraEducation?: IEducation[];
  certifications?: ICertification[];
  applicantLinks?: IApplicantLinks[];
}

export interface IApplicant extends IBase {
  name: string;
  transformedToUserId?: string;
  lastname: string;
  email: string;
  phone: string;
  address: IAddress[];
  contact: IContact[];
  card: ICard[];
  recoveryPasswordToken: string;
  password: string;
  networkType: string;
  roles: IUserRoleVendor[];
  vendorList: IUserVendorListType[];
  selectedVendor: string;
  random4digits: number;
  active: boolean;
  profileImage: string;
  root: boolean;
  notes: IUserNote[];
  incomeExpectation?: number;
  incomeExpectationCurrency?: ECurrency;
  interviewCalifications?: number;
  culturalMatch?: number;
  technicalInterviewCalifications?: number;
  cvUrl?: string;
  cvName?: string;
  tags?: string[];
  deleted?: boolean;
  locationId: number;
  region: string;
  processes: IProcess[];
  postulations?: string[];
  selectedVendorId?: string;
  rut?: string;
  country?: string;
  englishLvl?: string;
  profiles?: string[];
  aiCVInfo?: IAICV;
  createdBy?: ICreatedBy;
  blackListed?: boolean;
  internalCandidate?: boolean;
  referredBy?: string;
}

export interface ICreatedBy {
  id?: string;
  name?: string;
  lastName?: string;
  email?: string;
  imageUrl?: string;
}

export interface IProcess {
  id: string;
  offerId: string;
  status: string;
  userId: string;
  user?: IApplicant;
  fitByCV?: {
    score: number;
    justifications: string;
  };
  offer?: {
    id: string;
    name: string;
  };
}

export interface IUserNote {
  id: string;
  note?: string;
  date?: Date;
  createdBy?: string;
}

export interface IAddress {
  id?: string;
  name?: string;
  info: string;
  street: string;
  number: string;
}

export interface IContact {
  id: string;
  type: string;
  number: string;
}

export interface ICard {
  id: string;
  creditCardType: string;
  last4CardDigits: string;
  tbkUser: string;
  authCode: string;
}

export interface IUserRoleVendor {
  role: string;
  vendorId: string;
}

export interface IUserVendorListType {
  id: string;
  name: string;
}

export interface IMassiveUploadResults {
  successfulUploaded: number;
  successfulUpdated: number;
  withUploadErrors: number;
  errors?: string[];
}

export enum EOfferState {
  DRAFT = "DRAFT",
  PUBLIC = "PUBLIC",
  CLOSED = "CLOSED",
  CANCELED = "CANCELED",
  STANDBY = "STANDBY",
  ALL = "ALL",
  DEFAULT = "DEFAULT"
}

export enum EContractType {
  FULL_TIME = "FULL_TIME",
  PART_TIME = "PART_TIME",
  FREELANCE = "FREELANCE",
  INTERNSHIP = "INTERNSHIP"
}

export enum OfferDefinitionType {
  NEW = "NEW",
  SUBSTITUTION = "SUBSTITUTION"
}

export enum EOfferType {
  NEW = "NEW",
  CLONE = "CLONE"
}

export enum EProcessOfferState {
  INIT = "INIT",
  TERMINAL = "TERMINAL",
  LETTER = "LETTER",
  INTERNAL = "INTERNAL"
}

export enum EProcessOfferStateExtended {
  DISCARDED = "Descartado",
  TECH_ASSESSMENT = "Evaluación Técnica",
  SENT_TO_CLIENT = "Enviado a Cliente",
  RECRUITMENT_INTERVIEW = "Entrevista Reclutamiento",
  CLIENT_TECH_ASSESSMENT = "Evaluación Técnica Cliente",
  TALENT_ASSESSMENT = "Evaluación Talent",
  TECH_INTERVIEW = "Entrevista Técnica",
  CLIENT_INTERVIEW = "Entrevista Cliente",
  INTERVIEW_WITH_TALENT = "Entrevista con Talent",
  INTERVIEW_WITH_CLIENT = "Entrevista con el Cliente"
}

export interface IProcessOffer {
  id: string;
  label: string;
  state?: EProcessOfferState;
  total?: number;
}

export interface IProcessLeapTime {
  startDate?: Date;
  endDate?: Date;
  duration?: number;
  user?: string;
  vendor?: string;
}

export interface IClient {
  id: string;
  name: string;
  userId: string;
}

export interface StateHistoryProps {
  oldState: string;
  newState: string;
  date: string;
  reasonToChangeState: string;
}

export enum ERequestType {
  EXTERNAL = "EXTERNAL",
  INTERNAL = "INTERNAL"
}

export interface IFileExtras {
  name?: string;
  blob64?: string;
  size?: string;
}

export interface IClient {
  id: string;
  name: string;
}

export interface IRequest extends IBase {
  name: string;
  description?: string;
  crmId: string;
  opportunityMargin: number;
  creator: IUser;
  maxRent: number;
  currency: ECurrency;
  type?: ERequestType;
  offers?: IOffers[];
  area?: IArea;
  files?: IFileExtras[];
  deadline?: Date;
  assignedRecruiter?: IUser;
  contractType?: EEmployeeContractType;
  clientScheduleStart?: string;
  clientScheduleEnd?: string;
  projectDuration: string;
  specialShifts: boolean;
  locationRestriction: boolean;
  seniority: string;
  client: IClient;
  serviceType: string;
  vacancies: string;
  country: string;
  modality: string;
  techStack: string[];
  englishLevel: string;
  requiresCertificationOrDegree?: string;
  keyFunctionsAndResponsibilities?: string;
  isEquipmentProvidedByClientOrUs: boolean;
  hasClientInterview?: boolean;
  processStagesAndClientEvaluators?: string;
  specialShiftsDetails?: string;
  locationRestrictionDetails?: string;
  changeHistory?: RequestChangedHistory[];
  notes: IUserNote[];
  requestDefinition?: OfferDefinitionType;
  requestDefinitionJustification?: string;
  identifier?: string;
  offerStatus?: EOfferState;
  baseCLP?: number;
  ceaTalentCLP?: number;
  brutoCLP?: number;
  topeMaximoCEA?: number;
  role?: string;
  ceaComercialUF?: number;
  ceaSeniority?: ECeaSeniorityStatus;
}

export interface RequestChangedHistory {
  updateBy: RequestHistoryUser;
  updateAt: Date;
  updateField: RequestHistoryField[];
}

export interface RequestHistoryUser {
  name: string;
  lastName: string;
  profileImage: string;
  email: string;
  role: string;
}

export interface RequestHistoryField {
  field: string;
  oldValue: string;
  newValue: string;
}

export interface IOffers {
  id: string;
  name: string;
  description: string;
  vancancy: number;
  area?: IArea;
  client?: IVendorClient;
  createdAt: string;
  publicEnd?: string;
  image?: string;
  state: EOfferState;
  stateHistory?: StateHistoryProps[];
  processOffer: IProcessOffer[];
  maxSalary?: number;
  currency?: ECurrency;
  requester?: IUser;
  definition?: OfferDefinitionType;
  fake?: boolean;
  requests?: IRequest[];
  tags?: string[];
  profiles?: string[];
  recruiter?: string;
  leapTimeAverage?: number;
  country?: string;
  contractType?: EContractType;
  vendor?: IVendor;
  quantityOfApplicants?: number;
  knowledges?: IDimension[];
}

export const OFFER_TYPES = [
  {
    label: "Todas",
    value: "ALL"
  },
  {
    label: "Borrador",
    value: "DRAFT"
  },
  {
    label: "Públicas",
    value: "PUBLIC"
  },
  {
    label: "Cerradas",
    value: "CLOSED"
  },
  {
    label: "Cancelada",
    value: "CANCELED"
  },
  {
    label: "En espera",
    value: "STANDBY"
  }
];

export const OFFER_DEFINITION = [
  {
    id: OfferDefinitionType.NEW,
    name: "Nueva"
  },
  {
    id: OfferDefinitionType.SUBSTITUTION,
    name: "Sustitución"
  }
];

export const CONTRACT_TYPE = [
  {
    id: EContractType.FULL_TIME,
    name: "Full Time"
  },
  {
    id: EContractType.PART_TIME,
    name: "Part Time"
  },
  {
    id: EContractType.FREELANCE,
    name: "Freelance"
  },
  {
    id: EContractType.INTERNSHIP,
    name: "Internship"
  }
];

export interface IItems {
  id: string;
  name: string;
  item?: IProcess[];
}

export interface IItem {
  items: IItems[];
  headers: IProcessOffer;
  offerId: string;
  allHeaders: IProcessOffer[];
}

export interface OfferPickerSelectProps {
  label: string;
  value: string;
  role?: string;
  quantityOfApplicants?: number;
}

export interface UserPDFProps {
  name?: string;
  lastname?: string;
  phone?: string;
  email?: string;
  cv?: string;
  filename?: string;
  key: string;
}

export interface MassiveUploadProps {
  offer: string;
}

// Country codes
export type SupportedCountries = "CL" | "PE";

// Chilean Healthcare Systems (ISAPRE + FONASA)
export enum ChileanHealthcareEnum {
  FONASA = "FONASA",
  CONSALUD = "CONSALUD",
  COLMENA = "COLMENA",
  CRUZ_BLANCA = "CRUZ_BLANCA",
  NUEVA_MASVIDA = "NUEVA_MASVIDA",
  BANMEDICA = "BANMEDICA",
  VIDA_TRES = "VIDA_TRES"
}

// Peruvian Healthcare Systems (EsSalud + EPS + SIS)
export enum PeruvianHealthcareEnum {
  ESSALUD = "ESSALUD",
  SIS = "SIS",
  RIMAC = "RIMAC",
  PACIFICO = "PACIFICO",
  MAPFRE = "MAPFRE",
  SANITAS = "SANITAS"
}

// Healthcare dictionary by country
export const HealthcareByCountry: Record<SupportedCountries, string[]> = {
  CL: Object.values(ChileanHealthcareEnum),
  PE: Object.values(PeruvianHealthcareEnum)
};

// Chilean AFPs
export enum ChileanAfpsEnum {
  HABITAT = "HABITAT",
  CUPRUM = "CUPRUM",
  CAPITAL = "CAPITAL",
  PLANVITAL = "PLANVITAL",
  MODELO = "MODELO",
  UNO = "UNO"
}

// Peruvian AFPs
export enum PeruvianAfpsEnum {
  INTEGRA = "INTEGRA",
  PRIMA = "PRIMA",
  PROFUTURO = "PROFUTURO",
  HABITAT = "HABITAT" // exists both in CL and PE
}

// AFP dictionary by country
export const AfpsByCountry: Record<SupportedCountries, string[]> = {
  CL: Object.values(ChileanAfpsEnum),
  PE: Object.values(PeruvianAfpsEnum)
};

// Chilean Banks
export enum ChileanBanksEnum {
  BANCO_DE_CHILE = "BANCO_DE_CHILE",
  BANCO_ESTADO = "BANCO_ESTADO",
  BANCO_SANTANDER = "BANCO_SANTANDER",
  BANCO_BCI = "BANCO_BCI",
  ITAU = "ITAU",
  SCOTIABANK = "SCOTIABANK",
  BBVA = "BBVA",
  SECURITY = "SECURITY",
  FALABELLA = "FALABELLA",
  RIPLEY = "RIPLEY",
  INTERNACIONAL = "INTERNACIONAL",
  BICE = "BICE",
  CONSORCIO = "CONSORCIO",
  MERCADO_PAGO = "MERCADO_PAGO"
}

// Peruvian Banks (example)
export enum PeruvianBanksEnum {
  BCP = "BCP",
  INTERBANK = "INTERBANK",
  BBVA_PERU = "BBVA_PERU",
  SCOTIABANK_PERU = "SCOTIABANK_PERU",
  BANBIF = "BANBIF",
  MI_BANCO = "MI_BANCO"
}

// Country → Banks dictionary
export const BanksByCountry: Record<string, string[]> = {
  CL: Object.values(ChileanBanksEnum),
  PE: Object.values(PeruvianBanksEnum)
};

interface Contact {
  id: string;
  type: string;
  number: string;
}

interface ChildInput {
  name: string;
  birthDate: string;
  identityDocument: string;
}

export interface TransformFormProps {
  // Basic Personal Information
  firstName: string;
  firstLastName: string;
  secondLastName: string;
  identityDocument: string;
  birthDate: string;
  cleverId: string;
  // Personal Information
  maritalStatus: string;
  gender: string;
  birthCountry: string;
  residenceCountry: string;
  residenceCity: string;
  address: string;
  personalEmail: string;
  phoneNumber: string;
  // Work Information
  division: string;
  area: string;
  manager: string;
  position: string;
  seniority: string;
  collaboratorEmail: string;
  headEmail: string;
  managerEmail: string;
  // Banking Information
  bankAccountType: string;
  accountNumber: string;
  // Chilean Specific Information
  afp: string;
  healthSystem: string;
  healthSystemName?: string;
  isapre?: string;
  isapreAmount?: number;
  // Payment Information
  paymentPlatform: string;
  workCountry: string;
  workModality: string;
  contractType: string;
  startDate: string;
  computerDeliveryDate?: string;
  registrationDate?: string;
  baseSalary: number;
  grossSalary: number;
  currency: string;
  // Operational Data
  assignableToProjects?: boolean;
  isSubstitution?: boolean;
  substitutionFor?: string;
  requiresComputer?: boolean;
  clientProvidesComputer?: boolean;
  requiresCellPhone?: boolean;
  computerBonusApplies?: boolean;
  // Equipment Information
  computerType?: string;
  computerBrand?: string;
  serialNumber?: string;
  operatingSystem?: string;
  mobileOS?: string;
  // Company Information
  cleveritEmail?: string;
  cleveritLogin?: string;
  temporaryPassword?: string;
  // Additional Information
  dietaryConsiderations?: string;
  tshirtSize: string;
  profession: string;
  emergencyContacts: Contact[];
  children: ChildInput[];
  bankName?: string;
  extraArchives?: string[];

  // Require Stuff
  requireComputer?: boolean;

  // New Cloud Requirements Fields
  requiresComputerShipping?: boolean; // ¿Se requiere envío de computadora?
  nationality?: string; // Nacionalidad del nuevo colaborador
  upn?: string; // UPN (User Principal Name)
  password?: string; // Password

  seniorityLevel?: string; // Seniority Level

  // Social Networks
  linkedin?: string; // LinkedIn profile URL
  facebook?: string; // Facebook profile URL
  instagram?: string; // Instagram profile URL
}
